import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { catchError, Observable, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface CardifCommissionOptionApiItem {
  id: string;
  libelle: string;
  defaut?: boolean;
}

export type CommissionOptionApiItem = string | CardifCommissionOptionApiItem;

export interface ContactLookupData {
  type_reprise?: string;
  type_souscripteur?: string;
  civilite?: string;
  nom?: string;
  prenom?: string;
  date_naissance?: string;
  professional_category?: string;
  profession?: string;
  adresse?: string;
  complement_adresse?: string;
  code_postal?: string;
  ville?: string;
  pays?: string;
  telephone?: string;
  email?: string;
  deplacement_professionnel?: string;
  travaille_en_hauteur?: string;
  port_charge?: boolean;
  fumeur?: boolean;
  en_cours_total_assure?: number;
}

export interface ContactLookupResult {
  found: boolean;
  match_type?: 'email' | 'phone' | 'both';
  project_id?: string;
  civilite?: string;
  nom?: string;
  prenom?: string;
  email?: string;
  telephone?: string;
  contact_data?: ContactLookupData;
}

@Injectable({
  providedIn: 'root'
})
export class ComparaisonService {

  private apiUrl = `${environment.apiUrl}${environment.apiEndpoint}/comparaison`;

  constructor(private http: HttpClient) { }

  private buildHeadersWithOptionalToken(baseHeaders: Record<string, string> = {}): HttpHeaders {
    const token = localStorage.getItem('access_token');
    let headers = new HttpHeaders(baseHeaders);
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  }

private buildAprilRequestBody(
  formData: any,
  aprilSelection?: AprilSelectionPayload,
  aprilCommissionCode?: string
): any {
  if (!aprilSelection && !aprilCommissionCode) {
    return formData;
  }

  const payload: any = {
    form: formData,
  };

  if (aprilSelection) {
    payload.aprilSelection = aprilSelection;
  }

  if (aprilCommissionCode) {
    payload.aprilCommissionCode = aprilCommissionCode;
  }

  return payload;
}

validateEmailWithAbstract(email: string): Observable<EmailValidationResult> {
  const url = `${this.apiUrl}/email/validate`;
  const headers = this.buildHeadersWithOptionalToken({
    'Content-Type': 'application/json'
  });

  return this.http.post<EmailValidationResult>(url, { email }, { headers }).pipe(
    catchError(error => {
      console.error('Error validating email with AbstractAPI:', error);
      return throwError(() => error);
    })
  );
}

lookupExistingContact(
  email?: string,
  phone?: string,
  excludeProjectId?: string
): Observable<ContactLookupResult> {
  const params: Record<string, string> = {};
  const normalizedEmail = (email || '').trim();
  const normalizedPhone = (phone || '').trim();
  const excluded = (excludeProjectId || '').trim();

  if (normalizedEmail) {
    params['email'] = normalizedEmail;
  }
  if (normalizedPhone) {
    params['phone'] = normalizedPhone;
  }
  if (excluded) {
    params['exclude_project_id'] = excluded;
  }

  const headers = this.buildHeadersWithOptionalToken();
  return this.http.get<ContactLookupResult>(`${this.apiUrl}/contact/lookup`, {
    headers,
    params
  }).pipe(
    catchError(error => {
      console.error('Error looking up existing contact:', error);
      return throwError(() => error);
    })
  );
}

comparer(form: any): Observable<any> {
  const headers = this.buildHeadersWithOptionalToken();

  return this.http.post<any>(this.apiUrl, form, { headers });
}

submitPublicBorrowerForm(form: any): Observable<PublicBorrowerSubmissionResponse> {
  const url = `${this.apiUrl}/public/devis-emprunteur/submit`;
  const headers = this.buildHeadersWithOptionalToken({
    'Content-Type': 'application/json'
  });

  return this.http.post<PublicBorrowerSubmissionResponse>(url, form, { headers }).pipe(
    catchError(error => {
      console.error('Error submitting public borrower form:', error);
      return throwError(() => error);
    })
  );
}

async compareWithAllProviders(formData: any): Promise<any> {
  return new Promise((resolve, reject) => {
    this.comparer(formData).subscribe({
      next: (result) => resolve(result),
      error: (error) => reject(error)
    });
  });
}

genererDevisApril(
  formData: any,
  aprilSelection?: AprilSelectionPayload,
  aprilCommissionCode?: string
): Observable<{ success: boolean, redirect_url: string }> {
  const url = `${environment.apiUrl}${environment.apiEndpoint}/borrower/april/devis`;
  const token = localStorage.getItem('access_token');
  const headers = new HttpHeaders({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  });

  return this.http.post<{ success: boolean, redirect_url: string }>(
    url,
    this.buildAprilRequestBody(formData, aprilSelection, aprilCommissionCode),
    { headers }
  ).pipe(
    catchError(error => {
      console.error('Error generating April devis:', error);
      return throwError(() => error);
    })
  );
}

// Dans comparaison.service.ts - Remplacer genererDevisCardif par :
genererDevisCardif(formData: any): Observable<Blob> {
  const url = `${environment.apiUrl}${environment.apiEndpoint}/cardif/devis`;
  const token = localStorage.getItem('access_token');
  
  // Ajouter l'authentification manquante
  const headers = new HttpHeaders({
    'Authorization': `Bearer ${token}`,
    'Accept': 'application/pdf',
    'Content-Type': 'application/json'
  });

  return this.http.post(url, formData, {
    responseType: 'blob',
    headers: headers
  }).pipe(
    catchError(error => {
      console.error('Error generating Cardif quote:', error);
      return throwError(() => error);
    })
  );
}
// ...dans comparaison.service.ts...
sendUtwinQuoteByEmail(refDevis: string, formData: any): Observable<{ success: boolean }> {
  const url = `${environment.apiUrl}${environment.apiEndpoint}/utwin/send-devis-email`;
  const token = localStorage.getItem('access_token');
  console.log('Token utilisé pour PDF April:', token); // Ajoute ceci pour vérifier

  const headers = new HttpHeaders({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  });

  const payload = {
    refDevis: refDevis,
    form_data: formData
  };

  return this.http.post<{ success: boolean }>(url, payload, { headers }).pipe(
    catchError(error => {
      console.error('Error sending Utwin quote by email:', error);
      return throwError(() => error);
    })
  );
}

prepareUtwinSubscription(
  refDevis: string,
  formData: any,
  etudeMedicale: boolean = false
): Observable<{ success: boolean; errors?: string[]; providerResponse?: any }> {
  const url = `${environment.apiUrl}${environment.apiEndpoint}/utwin/set-transmission`;
  const token = localStorage.getItem('access_token');

  const headers = new HttpHeaders({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  });

  const payload = {
    refDevis: refDevis,
    form_data: formData,
    etudeMedicale
  };

  return this.http.post<{ success: boolean; errors?: string[]; providerResponse?: any }>(
    url,
    payload,
    { headers }
  ).pipe(
    catchError(error => {
      console.error('Error preparing Utwin subscription:', error);
      return throwError(() => error);
    })
  );
}

sendAmortissementEmail(payload: any): Observable<{ success: boolean }> {
  const url = `${environment.apiUrl}${environment.apiEndpoint}/comparaison/amortissement/send-email`;
  const token = localStorage.getItem('access_token');

  const headers = new HttpHeaders({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  });

  return this.http.post<{ success: boolean }>(url, payload, { headers }).pipe(
    catchError(error => {
      console.error('Error sending amortissement email:', error);
      return throwError(() => error);
    })
  );
}

sendSelectedQuotesEmail(payload: any): Observable<{ success: boolean }> {
  const url = `${environment.apiUrl}${environment.apiEndpoint}/comparaison/devis/send-email`;
  const token = localStorage.getItem('access_token');

  const headers = new HttpHeaders({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  });

  return this.http.post<{ success: boolean }>(url, payload, { headers }).pipe(
    catchError(error => {
      console.error('Error sending selected quotes email:', error);
      return throwError(() => error);
    })
  );
}

sendStepOneFollowUpEmail(payload: any): Observable<{ success: boolean }> {
  const url = `${environment.apiUrl}${environment.apiEndpoint}/comparaison/step-one/send-email`;
  const headers = this.buildHeadersWithOptionalToken({
    'Content-Type': 'application/json'
  });

  return this.http.post<{ success: boolean }>(url, payload, { headers }).pipe(
    catchError(error => {
      console.error('Error sending step-one follow-up email:', error);
      return throwError(() => error);
    })
  );
}

sendStepOneSavingsEmail(payload: any): Observable<{ success: boolean }> {
  const url = `${environment.apiUrl}${environment.apiEndpoint}/comparaison/step-one/send-savings-email`;
  const headers = this.buildHeadersWithOptionalToken({
    'Content-Type': 'application/json'
  });

  return this.http.post<{ success: boolean }>(url, payload, { headers }).pipe(
    catchError(error => {
      console.error('Error sending step-one savings email:', error);
      return throwError(() => error);
    })
  );
}

  // Ajouter cette méthode dans ComparaisonService
genererPdfApril(
  formData: any,
  productCode?: string,
  contributionType?: string,
  aprilSelection?: AprilSelectionPayload,
  aprilCommissionCode?: string
): Observable<{ success: boolean, documents: any[], message?: string }> {
  let url = `${environment.apiUrl}${environment.apiEndpoint}/borrower/april/pdf`;
  const params: string[] = [];
  if (productCode) params.push(`productCode=${encodeURIComponent(productCode)}`);
  if (contributionType) params.push(`contributionType=${encodeURIComponent(contributionType)}`);
  if (params.length) url += `?${params.join('&')}`;

  const token = localStorage.getItem('access_token');
  const headers = new HttpHeaders({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  });

  return this.http.post<{ success: boolean, documents: any[], message?: string }>(
    url,
    this.buildAprilRequestBody(formData, aprilSelection, aprilCommissionCode),
    { headers }
  );
}
downloadAprilPdf(url: string): Observable<Blob> {
  const token = localStorage.getItem('access_token');
  const headers = new HttpHeaders({
    'Authorization': `Bearer ${token}`
  });
  return this.http.get(url, { headers, responseType: 'blob' });
}

downloadAprilPdfViaBackend(url: string): Observable<Blob> {
  const token = localStorage.getItem('access_token');
  const headers = new HttpHeaders({
    'Authorization': `Bearer ${token}`
  });
  // Encode l'URL pour la passer en paramètre GET
  return this.http.get(`${environment.apiUrl}${environment.apiEndpoint}/borrower/april/download/april-pdf?url=${encodeURIComponent(url)}`, { headers, responseType: 'blob' });
}

creerProjetApril(
  formData: any,
  aprilSelection?: AprilSelectionPayload,
  aprilCommissionCode?: string
): Observable<{ url: string }> {
  const url = `${environment.apiUrl}${environment.apiEndpoint}/borrower/projects`;
  const token = localStorage.getItem('access_token');
  const headers = new HttpHeaders({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  });

  return this.http.post<{ url: string }>(
    url,
    this.buildAprilRequestBody(formData, aprilSelection, aprilCommissionCode),
    { headers }
  ).pipe(
    catchError(error => {
      console.error('Error creating April project:', error);
      return throwError(() => error);
    })
  );
}

creerProjetAfiesca(formData: any): Observable<{ url: string }> {
  const url = `${environment.apiUrl}${environment.apiEndpoint}/afiesca/projects`;
  const token = localStorage.getItem('access_token');
  const headers = new HttpHeaders({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  });

  return this.http.post<{ url: string }>(url, formData, { headers }).pipe(
    catchError(error => {
      console.error('Error creating Afiesca project:', error);
      return throwError(() => error);
    })
  );
}

downloadUtwinPdf(refDevis: string, formData: any): Observable<Blob> {
  const url = `${environment.apiUrl}${environment.apiEndpoint}/utwin/pdf/download`;
  const token = localStorage.getItem('access_token');
  const headers = new HttpHeaders({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  });

  const payload = {
    refDevis: refDevis,
    form_data: formData
  };

  return this.http.post(url, payload, { headers, responseType: 'blob' });
}

generateTop10ResultsPdf(results: any[]): Observable<Blob> {
  // Adapte l’URL selon ton backend
  return this.http.post(
    `${environment.apiUrl}${environment.apiEndpoint}/comparaison/pdf/top10`,
    { results },
    { responseType: 'blob' }
  );
}

  genererDevisNeoliane(formData: any, produitId: string, productData?: any): Observable<Blob> {
    const url = `${environment.apiUrl}${environment.apiEndpoint}/neoliane/devis`;
    const token = localStorage.getItem('access_token');
    
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Accept': 'application/pdf',
      'Content-Type': 'application/json'
    });

    const payload = {
      ...formData,
      produitId: produitId,
      productData: productData
    };

    return this.http.post(url, payload, {
      responseType: 'blob',
      headers: headers
    }).pipe(
      catchError(error => {
        console.error('Error generating Neoliane quote:', error);
        return throwError(() => error);
      })
    );
  }

  downloadNeolianeDocument(documentId: string): Observable<Blob> {
    const url = `${environment.apiUrl}${environment.apiEndpoint}/neoliane/documents/${documentId}`;
    const token = localStorage.getItem('access_token');
    
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`
    });

    return this.http.get(url, {
      responseType: 'blob',
      headers: headers
    }).pipe(
      catchError(error => {
        console.error('Error downloading Neoliane document:', error);
        return throwError(() => error);
      })
    );
  }

genererDevisZenioo(formData: any, productCode: string, productData?: any): Observable<Blob> {
  const url = `${environment.apiUrl}${environment.apiEndpoint}/zenioo/generer-devis-pdf`;
  const token = localStorage.getItem('access_token');
  
  const headers = new HttpHeaders({
    'Authorization': `Bearer ${token}`,
    'Accept': 'application/pdf',
    'Content-Type': 'application/json'
  });

  const payload = {
    ...formData,
    product_code: productCode,
    productData: productData || null  // Inclure les données du produit pour la tarification
  };

  return this.http.post(url, payload, {
    responseType: 'blob',
    headers: headers
  }).pipe(
    catchError(error => {
      console.error('Error generating Zenioo quote:', error);
      return throwError(() => error);
    })
  );
}

genererDevisLifesquare(formData: any, productCode: string | number, productData?: any): Observable<Blob> {
  const url = `${environment.apiUrl}${environment.apiEndpoint}/lifesquare/generer-devis-pdf`;
  const token = localStorage.getItem('access_token');

  const headers = new HttpHeaders({
    'Authorization': `Bearer ${token}`,
    'Accept': 'application/pdf',
    'Content-Type': 'application/json'
  });

  const payload = {
    ...formData,
    product_code: productCode,
    productData: productData || null
  };

  return this.http.post(url, payload, {
    responseType: 'blob',
    headers: headers
  }).pipe(
    catchError(error => {
      console.error('Error generating Lifesquare quote:', error);
      return throwError(() => error);
    })
  );
}

envoyerDevisZeniooParEmail(formData: any, productCode: string, email: string): Observable<{ success: boolean }> {
  const url = `${environment.apiUrl}${environment.apiEndpoint}/zenioo/envoyer-devis-email`;
  const token = localStorage.getItem('access_token');
  
  const headers = new HttpHeaders({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  });

  const payload = {
    ...formData,
    product_code: productCode,
    email: email
  };

  return this.http.post<{ success: boolean }>(url, payload, { headers }).pipe(
    catchError(error => {
      console.error('Error sending Zenioo quote by email:', error);
      return throwError(() => error);
    })
  );
}

/**
 * Souscription Zenioo - Obtient un lien vers le formulaire de souscription Soizic
 */
souscrireZenioo(formData: any, codeProduit: string, projectId?: string): Observable<any> {
  const url = `${environment.apiUrl}${environment.apiEndpoint}/zenioo/subscription/from-comparison`;
  const token = localStorage.getItem('access_token');

  const headers = new HttpHeaders({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  });

  const payload = {
    form: formData,
    code_produit: codeProduit,
    project_id: projectId || null
  };

  return this.http.post<any>(url, payload, { headers }).pipe(
    catchError(error => {
      console.error('Error subscribing Zenioo:', error);
      return throwError(() => error);
    })
  );
}

/**
 * Souscription Neoliane - Importe le projet dans Procento et retourne l'URL
 */
souscrireNeoliane(formData: any, produitId: string, projectId?: string): Observable<any> {
  const url = `${environment.apiUrl}${environment.apiEndpoint}/neoliane/subscription/from-comparison`;
  const token = localStorage.getItem('access_token');

  const headers = new HttpHeaders({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  });

  const payload = {
    form: formData,
    produit_id: produitId,
    project_id: projectId || null
  };

  return this.http.post<any>(url, payload, { headers }).pipe(
    catchError(error => {
      console.error('Error subscribing Neoliane:', error);
      return throwError(() => error);
    })
  );
}

/**
 * Souscription Cardif - Crée un dossier dans Sésame et retourne le lien d'accès
 */
souscrireCardif(formData: any, codeProduit: string, projectId?: string): Observable<any> {
  const url = `${environment.apiUrl}${environment.apiEndpoint}/cardif/subscription/from-comparison`;
  const token = localStorage.getItem('access_token');

  const headers = new HttpHeaders({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  });

  const payload = {
    form: formData,
    code_produit: codeProduit,
    project_id: projectId || null
  };

  return this.http.post<any>(url, payload, { headers }).pipe(
    catchError(error => {
      console.error('Error subscribing Cardif:', error);
      return throwError(() => error);
    })
  );
}

/**
 * Souscription LifeSquare - Crée une souscription et retourne l'URL de la fiche
 */
souscrireLifesquare(formData: any, offerId: string | number, projectId?: string, productData?: any): Observable<any> {
  const url = `${environment.apiUrl}${environment.apiEndpoint}/lifesquare/subscription/from-comparison`;
  const token = localStorage.getItem('access_token');

  const headers = new HttpHeaders({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  });

  const parsedOfferId = Number(offerId);
  const payload: any = {
    form: formData,
    project_id: projectId || null,
    offer_id: Number.isFinite(parsedOfferId) ? parsedOfferId : offerId,
    product: productData || null
  };

  return this.http.post<any>(url, payload, { headers }).pipe(
    catchError(error => {
      console.error('Error subscribing LifeSquare:', error);
      return throwError(() => error);
    })
  );
}

updateLifesquareCommission(
  borrowerId: string | number,
  borrowerPriceId: string | number,
  payload: LifesquareCommissionPayload
): Observable<any> {
  const url = `${environment.apiUrl}${environment.apiEndpoint}/lifesquare/borrowers/${borrowerId}/prices/${borrowerPriceId}/commission`;
  const token = localStorage.getItem('access_token');

  const headers = new HttpHeaders({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  });

  return this.http.put<any>(url, payload, { headers }).pipe(
    catchError(error => {
      console.error('Error updating LifeSquare commission:', error);
      return throwError(() => error);
    })
  );
}

/**
 * Récupérer les options de commission pour un partenaire
 */
getCommissionOptions(partner: string): Observable<CommissionOptionApiItem[]> {
  const token = localStorage.getItem('access_token');
  const headers = new HttpHeaders({
    'Authorization': `Bearer ${token}`
  });
  return this.http.get<CommissionOptionApiItem[]>(
    `${this.apiUrl}/commission_options/${partner}`,
    { headers }
  ).pipe(
    catchError(error => {
      console.error(`Error getting commission options for ${partner}:`, error);
      return throwError(() => error);
    })
  );
}

/**
 * Récupérer les paramètres de commission actuels
 */
getCommissionSettings(): Observable<{ [key: string]: string }> {
  const token = localStorage.getItem('access_token');
  const headers = new HttpHeaders({
    'Authorization': `Bearer ${token}`
  });
  return this.http.get<{ [key: string]: string }>(
    `${this.apiUrl}/commission_settings`,
    { headers }
  ).pipe(
    catchError(error => {
      console.error('Error getting commission settings:', error);
      return throwError(() => error);
    })
  );
}

/**
 * Lancer une comparaison avec des commissions personnalisées (override temporaire)
 */
comparerWithCommissions(form: any, commissions: { [key: string]: string }): Observable<any> {
  const token = localStorage.getItem('access_token');
  const headers = new HttpHeaders({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  });

  const payload = {
    ...form,
    commission_overrides: commissions
  };

  return this.http.post<any>(this.apiUrl, payload, { headers }).pipe(
    catchError(error => {
      console.error('Error comparing with custom commissions:', error);
      return throwError(() => error);
    })
  );
}

// ============================================================================
// AFI-ESCA / PERENIM - Génération de devis
// ============================================================================

/**
 * Génère un devis AFI-ESCA via SaveDossierSimulation
 * @param formData Les données du formulaire de comparaison
 * @returns Observable avec l'ID de simulation et les documents disponibles
 */
genererDevisAfiesca(formData: any): Observable<AfiescaDevisResponse> {
  const url = `${environment.apiUrl}${environment.apiEndpoint}/comparaison/afiesca/devis`;
  const token = localStorage.getItem('access_token');
  
  const headers = new HttpHeaders({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  });

  return this.http.post<AfiescaDevisResponse>(url, formData, { headers }).pipe(
    catchError(error => {
      console.error('Error generating AFI-ESCA devis:', error);
      return throwError(() => error);
    })
  );
}

/**
 * Télécharge un document PDF AFI-ESCA (projet de tarification, proposition, etc.)
 * @param simulationId L'identifiant de la simulation
 * @param typeDocument Le type de document à télécharger
 * @returns Observable<Blob> Le PDF
 */
telechargerDocumentAfiesca(simulationId: string, typeDocument: AfiescaDocumentType): Observable<Blob> {
  const url = `${environment.apiUrl}${environment.apiEndpoint}/comparaison/afiesca/devis/${simulationId}/document/${typeDocument}`;
  const token = localStorage.getItem('access_token');
  
  const headers = new HttpHeaders({
    'Authorization': `Bearer ${token}`,
    'Accept': 'application/pdf'
  });

  return this.http.get(url, {
    responseType: 'blob',
    headers: headers
  }).pipe(
    catchError(error => {
      console.error('Error downloading AFI-ESCA document:', error);
      return throwError(() => error);
    })
  );
}

/**
 * Télécharge un document PDF AFI-ESCA encodé en base64
 * @param simulationId L'identifiant de la simulation
 * @param typeDocument Le type de document à télécharger
 * @returns Observable avec le contenu base64
 */
telechargerDocumentAfiescaBase64(simulationId: string, typeDocument: AfiescaDocumentType): Observable<AfiescaDocumentBase64Response> {
  const url = `${environment.apiUrl}${environment.apiEndpoint}/afiesca/devis/${simulationId}/document/${typeDocument}/base64`;
  const token = localStorage.getItem('access_token');
  
  const headers = new HttpHeaders({
    'Authorization': `Bearer ${token}`
  });

  return this.http.get<AfiescaDocumentBase64Response>(url, { headers }).pipe(
    catchError(error => {
      console.error('Error downloading AFI-ESCA document (base64):', error);
      return throwError(() => error);
    })
  );
}

/**
 * Récupère les détails d'une simulation AFI-ESCA existante
 * @param simulationId L'identifiant de la simulation
 * @returns Observable avec les détails de la simulation
 */
getSimulationAfiesca(simulationId: string): Observable<any> {
  const url = `${environment.apiUrl}${environment.apiEndpoint}/afiesca/devis/${simulationId}`;
  const token = localStorage.getItem('access_token');
  
  const headers = new HttpHeaders({
    'Authorization': `Bearer ${token}`
  });

  return this.http.get<any>(url, { headers }).pipe(
    catchError(error => {
      console.error('Error getting AFI-ESCA simulation:', error);
      return throwError(() => error);
    })
  );
}

/**
 * Liste les simulations AFI-ESCA
 * @param recherche Filtre optionnel
 * @param top Nombre d'éléments
 * @param skip Pagination
 * @returns Observable avec la liste des simulations
 */
getSimulationsAfiesca(recherche?: string, top: number = 30, skip: number = 0): Observable<any[]> {
  const url = `${environment.apiUrl}${environment.apiEndpoint}/afiesca/simulations`;
  const token = localStorage.getItem('access_token');
  
  const headers = new HttpHeaders({
    'Authorization': `Bearer ${token}`
  });

  let params: any = { top, skip };
  if (recherche) {
    params.recherche = recherche;
  }

  return this.http.get<any[]>(url, { headers, params }).pipe(
    catchError(error => {
      console.error('Error listing AFI-ESCA simulations:', error);
      return throwError(() => error);
    })
  );
}

/**
 * Recalcule la tarification sur une simulation AFI-ESCA
 * @param simulationId L'identifiant de la simulation
 * @returns Observable avec le résultat du recalcul
 */
recalculerTarificationAfiesca(simulationId: string): Observable<any> {
  const url = `${environment.apiUrl}${environment.apiEndpoint}/afiesca/devis/${simulationId}/tarification`;
  const token = localStorage.getItem('access_token');
  
  const headers = new HttpHeaders({
    'Authorization': `Bearer ${token}`
  });

  return this.http.put<any>(url, {}, { headers }).pipe(
    catchError(error => {
      console.error('Error recalculating AFI-ESCA tarification:', error);
      return throwError(() => error);
    })
  );
}

/**
 * Récupère la liste des types de documents disponibles
 * @returns Observable avec les types de documents
 */
getTypesDocumentsAfiesca(): Observable<AfiescaDocumentTypeInfo[]> {
  const url = `${environment.apiUrl}${environment.apiEndpoint}/afiesca/documents/types`;
  const token = localStorage.getItem('access_token');
  
  const headers = new HttpHeaders({
    'Authorization': `Bearer ${token}`
  });

  return this.http.get<AfiescaDocumentTypeInfo[]>(url, { headers }).pipe(
    catchError(error => {
      console.error('Error getting AFI-ESCA document types:', error);
      return throwError(() => error);
    })
  );
}
}

// ============================================================================
// Interfaces AFI-ESCA
// ============================================================================

export interface AfiescaDevisResponse {
  success: boolean;
  numero_dossier?: string;
  simulation_id?: string;
  dossiers?: AfiescaDossier[];
  documents_disponibles?: string[];
  error_description?: string;
}

export interface AfiescaDossier {
  numero_dossier: string;
  nom: string;
  prenom: string;
  url: string;
}

export type AfiescaDocumentType = 
  | 'PROJET_TARIFICATION'
  | 'PROPOSITION_ASSURANCE'
  | 'PROPOSITION_ASSURANCE_ASSURE1'
  | 'PROPOSITION_ASSURANCE_ASSURE2'
  | 'LETTRE_DE_MISSION_ASSURE1'
  | 'LETTRE_DE_MISSION_ASSURE2'
  | 'FICHE_STANDARDISEE';

export interface AfiescaDocumentBase64Response {
  success: boolean;
  content_type?: string;
  filename?: string;
  content_base64?: string;
  error_description?: string;
}

export interface AfiescaDocumentTypeInfo {
  code: string;
  description: string;
}

export interface EmailValidationResult {
  valid: boolean;
  reason: string;
  message: string;
  deliverability?: string | null;
  quality_score?: number | null;
  is_disposable?: boolean | null;
}

export interface PublicBorrowerSubmissionResponse {
  success: boolean;
  project_id: string;
  message: string;
  email_status?: {
    advisor_sent?: boolean;
    client_sent?: boolean;
  };
}

export interface LifesquareCommissionPayload {
  commissionRate: string;
  commissionType: string;
  brokerFees: number;
  applicationFeesStaggered: boolean;
}

export interface AprilBorrowerSelectionPayload {
  productCode: string;
  contributionType: string;
}

export interface AprilSelectionPayload {
  mainBorrower: AprilBorrowerSelectionPayload;
  coBorrower?: AprilBorrowerSelectionPayload;
}
